// A room's fixtures on the plan: the selected one (a backrest or a light), the
// Backrest tool and a backrest's panel, and the Bench end tool that closes or
// opens a bench's end. docs/claude/lights.md, docs/claude/benches.md.
import {
  type Backrest,
  type BackrestHost,
  type ProductInstance,
  type RoomLight,
  type SculptedBench,
  type Vec2,
} from '../../shared/types';
import {
  backrestAt,
  backrestBand,
  backrestBenchAt,
  backrestBenchOf,
  backrestColor,
  backrestFigures,
  backrestHostAt,
  backrestPlan,
  backrestRun,
  backrestsOf,
  backrestSpan,
  backrestSpanFor,
  type BackrestWarning,
  backrestWarnings,
  orphanBackrests,
  sameHost,
} from '../backrest';
import {
  backrestCutsOf,
  benchRestBase,
} from '../benchRest';
import {
  type BenchEndMark,
  benchEndMarks,
  type BenchEndSide,
} from '../benchSolids';
import {
  supportFrame,
  supportHoverPolys,
} from './benchSupportTool';
import {
  changes,
  drawingRev,
  instLocked,
  invalidate,
  newId,
  objHidden,
  pushHistory,
  renderProperties,
  state,
  vp,
} from './core';
import {
  distance,
  pointInPolygon,
  toInstanceLocal,
} from '../geometry';
import { distToPolyline } from '../modifyOps';
import { tr } from '../i18n';
import {
  isFibreKit,
  unshareKits,
} from '../lightLife';
import {
  dropKitProjector,
  heaterFootprints,
  pickedLightsNow,
  renderLightPanel,
  renderLightsPanel,
} from './lightTools';
import {
  type BackrestLighting,
  backrestLighting,
  channelAt,
  channelFigures,
  channelsOf,
  circleRing,
  coveGeometry,
  coveOf,
  drawnStripPts,
  FIBRE_DIAMETER,
  lightKeysInOrder,
  pathOutline,
  pointNearPath,
  setBackrestLighting,
  skiesOf,
  skyFibreAt,
  skyZone,
  SPOT_DIAMETER,
  spotAt,
  spotsOf,
  staleLights,
  STRIP_APART_PX,
  STRIP_WIDTH,
  stripAt,
  stripPieces,
  syncChannels,
  updatedTagOrder,
} from '../lights';
import {
  buttonRow,
  checkboxRow,
  colorRow,
  deleteRow,
  infoRow,
  noteRow,
  numberRow,
  sectionTitle,
  selectRow,
} from './panelRows';
import {
  isPolyRoom,
  polyInnerCarved,
} from '../polyRoom';
import {
  dropFeature,
  featureOf,
  selectFeature,
  selectOne,
  selectRoomKeepingFeature,
} from '../selection';
import {
  type BenchHover,
  deselectAll,
  instancesAt,
  selectRef,
} from '../main';
import { type ObjRef } from '../pickStack';
import {
  orphanSculptedBenches,
  sculptBenchAt,
  sculptLayout,
} from '../sculptBench';
import { renderSculptBenchPanel } from './sculptBenchUi';
import { liveFeatureOf } from '../roomFeatures';
import { settleFloorGrilles } from '../floorGrille';
import { standsIn } from '../floorOccupancy';
import {
  aimedBench,
  pxAt,
  type ToolAim,
} from '../toolAim';

// ---- Room fixtures: backrests (the Backrest tool) ------------------------------

/** The selected fixture, while its room is the selection and can be edited
 *  (`liveFeatureOf`, roomFeatures.ts). */
export function selectedFixtureNow(): { inst: ProductInstance; id: string; backrest?: Backrest; light?: RoomLight; sculpted?: SculptedBench } | null {
  const f = liveFeatureOf(state, state.drawing.instances, 'fixture');
  if (!f || instLocked(f.room)) return null;
  const inst = f.room, id = f.sel.id;
  const backrest = inst.backrests?.find((b) => b.id === id);
  if (backrest) return { inst, id, backrest };
  const sculpted = inst.sculptedBenches?.find((b) => b.id === id);
  if (sculpted) return { inst, id, sculpted };
  const light = inst.lights?.find((l) => l.id === id);
  return light ? { inst, id, light } : null;
}

/** The fixture under a world point — a strip light (drawn over what it is
 *  fixed to), else a backrest's boards — in the topmost room that can be edited. */
export function fixtureHitTest(world: Vec2): { instId: string; id: string } | null {
  for (let i = state.drawing.instances.length - 1; i >= 0; i--) {
    const inst = state.drawing.instances[i];
    if (!inst.autoBenches || instLocked(inst) || !isPolyRoom(inst)) continue;
    const local = toInstanceLocal(inst, world);
    const br = backrestAt(inst, local, 4 / vp.scale);
    const sculpted = (): { instId: string; id: string } | null => { const sb = sculptBenchAt(inst, local, 4 / vp.scale); return sb ? { instId: inst.id, id: sb.id } : null; };
    if (state.disciplineFilters.lighting === false) { if (br) return { instId: inst.id, id: br.id }; const sb = sculpted(); if (sb) return sb; continue; }
    const spot = spotAt(inst, local, 4 / vp.scale);
    if (spot) return { instId: inst.id, id: spot.id };
    const fibre = skyFibreAt(inst, local, 4 / vp.scale);
    if (fibre) return { instId: inst.id, id: fibre.sky.id };
    const channel = channelAt(inst, local, 4 / vp.scale);
    if (channel) return { instId: inst.id, id: channel.id };
    const cove = coveOf(inst);
    if (cove && (coveGeometry(inst)?.runs ?? []).some((r) => pointNearPath(local, r.pts, STRIP_WIDTH / 2 + 4 / vp.scale))) return { instId: inst.id, id: cove.id };
    const strip = stripAt(inst, local, 4 / vp.scale, undefined, STRIP_APART_PX / vp.scale);
    if (strip) return { instId: inst.id, id: strip.id };
    if (br) return { instId: inst.id, id: br.id };
    const sb = sculpted();
    if (sb) return sb;
  }
  return null;
}

/** A fixture's plan outline (room local), what its hover and Parts row show: a
 *  backrest's boards, a strip's own part of its run (wide enough to see). */
export function fixtureOutline(inst: ProductInstance, id: string): Vec2[] | null {
  const br = inst.backrests?.find((b) => b.id === id);
  if (br) return backrestBand(inst, br) ?? backrestPlan(inst, br)[0] ?? null;
  const sb = inst.sculptedBenches?.find((b) => b.id === id);
  if (sb) return sculptLayout(inst, sb)?.outline ?? null;
  const spot = spotsOf(inst).find((l) => l.id === id);
  if (spot) return circleRing(spot.at, Math.max((spot.fibre ? FIBRE_DIAMETER : SPOT_DIAMETER) / 2, 7 / vp.scale));
  const channel = channelsOf(inst).find((l) => l.id === id);
  if (channel) return pathOutline(channel.pts, Math.max(channelFigures(channel).width, 6 / vp.scale));
  const sky = skiesOf(inst).find((l) => l.id === id);
  if (sky) return skyZone(inst, sky);
  const cove = coveOf(inst);
  if (cove?.id === id) {
    const runs = coveGeometry(inst)?.runs ?? [];
    const longest = runs.reduce<(typeof runs)[number] | null>((m, r) => (!m || r.length > m.length ? r : m), null);
    return longest ? pathOutline(longest.pts, Math.max(STRIP_WIDTH, 6 / vp.scale)) : null;
  }
  const piece = inst.lights?.some((l) => l.id === id) ? stripPieces(inst).find((p) => p.id === id) : undefined;
  return piece ? pathOutline(drawnStripPts(piece, STRIP_APART_PX / vp.scale), Math.max(STRIP_WIDTH, 6 / vp.scale)) : null;
}

/** Remove a fixture from its room (and from the selection). */
export function removeFixture(inst: ProductInstance, id: string): void {
  if (inst.backrests) {
    inst.backrests = inst.backrests.filter((b) => b.id !== id);
    if (!inst.backrests.length) delete inst.backrests;
  }
  if (inst.sculptedBenches) {
    inst.sculptedBenches = inst.sculptedBenches.filter((b) => b.id !== id);
    if (!inst.sculptedBenches.length) delete inst.sculptedBenches;
  }
  if (inst.lights) {
    const gone = inst.lights.find((l) => l.id === id);
    inst.lights = inst.lights.filter((l) => l.id !== id);
    if (!inst.lights.length) delete inst.lights;
    if (gone && isFibreKit(gone)) dropKitProjector(gone);
  }
  if (featureOf(state, 'fixture')?.id === id) dropFeature(state, 'fixture');
}

/** A press on a fixture at the head of the stack: select it, with its room. */
export function downFixture(head: ObjRef | null): boolean {
  if (head?.kind !== 'fixture' || !head.instId) return false;
  selectRef(head);
  return true;
}

let fixtureRevision = -1;
let fixtureDrawing: unknown = null;
/** What rests on the benches and what stands in the backrests follow them
 *  (benchRest.ts): a part resting on the benches (`benchRest`: an infrared
 *  emitter) sits on the highest seat under it — moved, or its benches raised —,
 *  and each room learns what the parts standing in it take out of its backrests
 *  and their strips (`backrestCuts`). Inside the edit's undo step, as the
 *  fixtures' own reconciling. */
function settleBenchRests(): void {
  settleFloorGrilles(state.drawing.instances);
  const rooms = state.drawing.instances.filter((i) => i.autoBenches && isPolyRoom(i));
  for (const p of state.drawing.instances) {
    if (p.benchRest == null) continue;
    const room = rooms.find((r) => standsIn(r, p));
    const base = room ? benchRestBase(room, p) : null;
    if (base != null && p.heightFromGround !== base) p.heightFromGround = base;
  }
  const cutters = state.drawing.instances.filter((i) => i.cutsBackrest && !objHidden(i.id));
  for (const r of rooms) {
    const cuts = cutters.length ? backrestCutsOf(r, cutters) : [];
    if (cuts.length) r.backrestCuts = cuts;
    else delete r.backrestCuts;
  }
}

/** A backrest whose host is gone — its wall, heater wall or partition deleted —
 *  goes with it, and so does a strip whose bench or backrest is gone (or whose
 *  position its host no longer offers), the frame after the edit, inside its
 *  undo step; a channel laid along walls follows them. A drawing loaded is
 *  reconciled too. */
export function reconcileFixtures(): void {
  if (state.dragMode !== 'none' || (drawingRev() === fixtureRevision && fixtureDrawing === state.drawing)) return;
  fixtureDrawing = state.drawing;
  settleBenchRests();
  for (const inst of state.drawing.instances) {
    if (inst.backrests?.length) {
      const gone = new Set(orphanBackrests(inst));
      if (gone.size) {
        inst.backrests = inst.backrests.filter((b) => !gone.has(b.id));
        if (!inst.backrests.length) delete inst.backrests;
      }
    }
    if (inst.sculptedBenches?.length) {
      const gone = new Set(orphanSculptedBenches(inst));
      if (gone.size) {
        inst.sculptedBenches = inst.sculptedBenches.filter((b) => !gone.has(b.id));
        if (!inst.sculptedBenches.length) delete inst.sculptedBenches;
      }
    }
    if (inst.lights?.length) {
      // A channel laid along walls follows them.
      syncChannels(inst);
      const gone = new Set(staleLights(inst));
      if (gone.size) {
        inst.lights = inst.lights.filter((l) => !gone.has(l.id));
        if (!inst.lights.length) delete inst.lights;
      }
    }
  }
  // The lights' tags: a new kind of light goes last, one gone closes up.
  const order = updatedTagOrder(state.drawing.lightTagOrder, lightKeysInOrder(state.drawing.instances));
  if (order.join('\n') !== (state.drawing.lightTagOrder ?? []).join('\n')) {
    if (order.length) state.drawing.lightTagOrder = order; else delete state.drawing.lightTagOrder;
  }
  // A fibre kit whose projector part is gone feeds from none; of two naming one,
  // the first keeps it.
  unshareKits(state.drawing.instances, new Set(state.drawing.instances.map((i) => i.id)));
  changes.touch();
  fixtureRevision = drawingRev();
}

/** What the Backrest tool does under a world point: remove the backrest under
 *  it, or the one over the bench in front of it, else add one over that bench
 *  (or along the face where none stands). */
function backrestToolTarget(aim: ToolAim): { room: ProductInstance; remove?: Backrest; host?: BackrestHost; bench?: string } | null {
  const world = aim.world;
  for (let i = state.drawing.instances.length - 1; i >= 0; i--) {
    const room = state.drawing.instances[i];
    if (!room.autoBenches || instLocked(room) || !isPolyRoom(room)) continue;
    const local = toInstanceLocal(room, world);
    const on = backrestAt(room, local, pxAt(aim, 4));
    if (on) return { room, remove: on };
    if (!pointInPolygon(local, polyInnerCarved(room))) continue;
    const host = backrestHostAt(room, local);
    if (!host) return null;
    const bench = backrestBenchAt(room, host, local);
    const there = backrestsOf(room).find((b) => sameHost(b.host, host) && backrestBenchOf(room, b) === bench);
    return there ? { room, remove: there } : { room, host, ...(bench ? { bench } : {}) };
  }
  return null;
}

/** The Backrest tool's hover: the backrest a click would add (green) or remove (red). */
export function backrestHoverAt(aim: ToolAim): BenchHover | null {
  const t = backrestToolTarget(aim);
  if (!t) return null;
  if (t.remove) {
    const polys = backrestPlan(t.room, t.remove);
    return polys.length ? { instId: t.room.id, polys, kind: 'delete' } : null;
  }
  const polys = t.host ? backrestPlan(t.room, { id: '', host: t.host, ...(t.bench ? { bench: t.bench } : {}) }) : [];
  return polys.length ? { instId: t.room.id, polys, kind: 'add' } : null;
}

// ---- Bench end tool ---------------------------------------------------------

/** How near a bench's end the Bench end tool finds it (mm). */
const BENCH_END_REACH = 150;

/** Where a bench's `closeStart` / `closeEnd` are stored: a room's wall bench or
 *  partition tier by its id, or the standalone sauna bench itself. */
function benchEndHost(inst: ProductInstance, bench: string): { closeStart?: boolean; closeEnd?: boolean } | null {
  if (inst.category === 'saunabench') return inst.id === bench ? inst : null;
  const wall = inst.polyBenches?.find((b) => b.id === bench);
  if (wall) return wall;
  for (const iw of inst.innerWalls ?? []) {
    const leg = [...(iw.benches?.a ?? []), ...(iw.benches?.b ?? [])].find((l) => l.id === bench);
    if (leg) return leg;
  }
  return null;
}

/** Close a bench's end, or open it. */
function setBenchEnd(host: { closeStart?: boolean; closeEnd?: boolean }, side: BenchEndSide, closed: boolean): void {
  const k = side === 'start' ? 'closeStart' : 'closeEnd';
  if (closed) host[k] = true; else delete host[k];
}

/** The Bench end tool's target: the end of a slatted bench nearest the cursor
 *  that can close (any but one another bench carries on from), within reach of
 *  where its boards stand — on a room or a standalone sauna bench under it that
 *  can be edited. */
function benchEndTargetAt(aim: ToolAim): { inst: ProductInstance; end: BenchEndMark } | null {
  const world = aim.world;
  const reach = Math.max(BENCH_END_REACH, pxAt(aim, 8));
  for (const id of instancesAt(world, false)) {
    const inst = state.drawing.instances.find((i) => i.id === id);
    if (!inst || instLocked(inst)) continue;
    if (!(isPolyRoom(inst) && inst.autoBenches) && inst.category !== 'saunabench') continue;
    const p = supportFrame(inst, world);
    let best: BenchEndMark | null = null, bestD = reach;
    // In 3D, the bench the click is on (the tiers' ends meet in the plan).
    const on = aimedBench(aim);
    for (const e of benchEndMarks(inst)) {
      if (on && e.bench !== on) continue;
      const d = pointInPolygon(p, e.poly) ? 0 : distToPolyline(p, e.poly, true);
      if (d <= bestD) { bestD = d; best = e; }
    }
    if (best) return { inst, end: best };
  }
  return null;
}

/** The Bench end tool's hover: the boards a click would stand (green) or take
 *  away (red), each its own thickness — drawn at least a few pixels thick, so a
 *  far zoom still shows them. */
export function benchEndHoverAt(aim: ToolAim): BenchHover | null {
  const t = benchEndTargetAt(aim);
  if (!t) return null;
  const polys = t.end.boards.map(([b, bi, fi, f]) => {
    const w = Math.max(distance(b, bi), pxAt(aim, 6));
    const along = (a: Vec2, c: Vec2): Vec2 => { const l = distance(a, c) || 1; return { x: a.x + ((c.x - a.x) * w) / l, y: a.y + ((c.y - a.y) * w) / l }; };
    return [b, along(b, bi), along(f, fi), f];
  });
  return { instId: t.inst.id, polys: supportHoverPolys(t.inst, polys), kind: t.end.closed ? 'delete' : 'add' };
}

export function downBenchEndTool(e: MouseEvent, aim: ToolAim): boolean {
  if (state.tool !== 'bench-end' || e.button !== 0) return false;
  const t = benchEndTargetAt(aim);
  const host = t ? benchEndHost(t.inst, t.end.bench) : null;
  if (!t || !host) return true;
  pushHistory();
  setBenchEnd(host, t.end.side, !t.end.closed);
  deselectAll();
  selectOne(state, 'instance', t.inst.id);
  if (t.inst.category !== 'saunabench') selectFeature(state, { kind: 'bench', instId: t.inst.id, benchId: t.end.bench });
  state.benchAddHover = benchEndHoverAt(aim);
  renderProperties();
  invalidate();
  return true;
}

/** A slatted bench's ends card: Close start, Close end — each greyed where the
 *  end cannot close (another bench carries on from it). */
export function appendBenchEndsCard(body: HTMLElement, inst: ProductInstance, bench: string, host: { closeStart?: boolean; closeEnd?: boolean }): void {
  const free = benchEndMarks(inst).filter((m) => m.bench === bench);
  body.appendChild(sectionTitle('Ends'));
  for (const side of ['start', 'end'] as const) {
    const on = free.find((m) => m.side === side);
    const row = checkboxRow(side === 'start' ? 'Close start' : 'Close end', !!on?.closed, (v) => {
      pushHistory();
      setBenchEnd(host, side, v);
      renderProperties();
      invalidate();
    });
    if (!on) {
      const box = row.querySelector('input')!;
      box.disabled = true;
      row.title = tr('Another bench carries on from this end');
    }
    body.appendChild(row);
  }
}

export function downBackrestTool(e: MouseEvent, aim: ToolAim): boolean {
  if (state.tool !== 'backrest' || e.button !== 0) return false;
  const t = backrestToolTarget(aim);
  if (!t) return true;
  pushHistory();
  if (t.remove) {
    removeFixture(t.room, t.remove.id);
    selectRoomKeepingFeature(state, t.room.id);
  } else if (t.host) {
    const br: Backrest = { id: newId('br'), host: t.host, ...(t.bench ? { bench: t.bench } : {}) };
    (t.room.backrests ??= []).push(br);
    deselectAll();
    selectOne(state, 'instance', t.room.id);
    selectFeature(state, { kind: 'fixture', instId: t.room.id, id: br.id });
  }
  renderProperties();
  invalidate();
  return true;
}

/** Where a backrest stands, as the panel says it: its wall, heater wall or
 *  partition face. */
function backrestHostLabel(inst: ProductInstance, host: BackrestHost): string {
  if ('wall' in host) return `${tr('wall')} ${host.wall + 1}`;
  if ('heaterWall' in host) return tr('Heater wall');
  const i = (inst.innerWalls ?? []).findIndex((w) => w.id === host.partition);
  return `${tr('inner wall')} ${i + 1} · ${host.face === 'a' ? 'A' : 'B'}`;
}

/** The selected fixture's own panel. */
export function renderFixturePanel(body: HTMLElement): boolean {
  const fx = selectedFixtureNow();
  if (!fx) return false;
  const many = pickedLightsNow();
  if (many) renderLightsPanel(body, many.inst, many.lights);
  else if (fx.backrest) renderBackrestPanel(body, fx.inst, fx.backrest);
  else if (fx.sculpted) renderSculptBenchPanel(body, fx.inst, fx.sculpted);
  else if (fx.light) renderLightPanel(body, fx.inst, fx.light);
  return true;
}

/** A backrest's panel: where it stands along its face, every figure of its
 *  boards, its colour, then Delete. */
function renderBackrestPanel(body: HTMLElement, inst: ProductInstance, br: Backrest): void {
  const f = backrestFigures(br);
  const run = backrestRun(inst, br);
  const edit = (fn: () => void): void => { pushHistory(); fn(); renderProperties(); invalidate(); };
  body.appendChild(sectionTitle('Backrest'));
  body.appendChild(infoRow('On', backrestHostLabel(inst, br.host)));
  if (run) {
    const { lo, hi } = run.face, len = run.s1 - run.s0;
    const place = (figure: Parameters<typeof backrestSpanFor>[1]): void => {
      const span = backrestSpanFor(run, figure);
      if (span) { br.start = span.start; br.end = span.end; } else { delete br.start; delete br.end; }
    };
    body.appendChild(numberRow('Width (mm)', Math.round(len), (v) => edit(() => place({ width: v })), { commit: true }));
    body.appendChild(numberRow('Distance from left (mm)', Math.round(run.s0 - lo), (v) => edit(() => place({ left: v })), { commit: true }));
    body.appendChild(numberRow('Distance from right (mm)', Math.round(hi - run.s1), (v) => edit(() => place({ right: v })), { commit: true }));
    if (br.start != null || br.end != null) body.appendChild(buttonRow(run.bench ? 'Follow the bench' : 'Span the whole wall', () => edit(() => { delete br.start; delete br.end; })));
  }
  const fig = (label: string, v: number, set: (x: number) => void): void => {
    body.appendChild(numberRow(label, v, (x) => edit(() => set(x)), { commit: true }));
  };
  fig('Boards', f.boards, (x) => { br.boards = Math.min(8, Math.max(1, Math.round(x))); });
  fig('Board height (mm)', f.boardHeight, (x) => { br.boardHeight = Math.max(10, Math.round(x)); });
  fig('Board thickness (mm)', f.boardThickness, (x) => { br.boardThickness = Math.max(5, Math.round(x)); });
  fig('Gap between boards (mm)', f.gap, (x) => { br.gap = Math.max(0, Math.round(x)); });
  fig('Height over the seat (mm)', f.overSeat, (x) => { br.overSeat = Math.max(0, Math.round(x)); });
  fig('Stand-off from the wall (mm)', f.standOff, (x) => { br.standOff = Math.max(0, Math.round(x)); });
  const span = backrestSpan(inst, br);
  body.appendChild(infoRow('Boards above the floor', `${Math.round(span.z0)} – ${Math.round(span.z1)} mm`));
  body.appendChild(selectRow('Light behind', BACKREST_LIGHTING_OPTIONS, backrestLighting(inst, br.id), (v) => edit(() => {
    setBackrestLighting(inst, br.id, v as BackrestLighting, () => newId('lt'));
  })));
  body.appendChild(colorRow('Colour', backrestColor(inst, br), (hex) => { br.color = hex; renderProperties(); invalidate(); }, {
    onClear: () => { pushHistory(); delete br.color; invalidate(); return backrestColor(inst, br); },
  }));
  for (const w of backrestWarnings(inst, br, heaterFootprints(inst))) body.appendChild(noteRow(BACKREST_WARNING_TEXT[w](), 'warn', true));
  body.appendChild(deleteRow(() => { pushHistory(); removeFixture(inst, br.id); renderProperties(); invalidate(); }));
}

/** The ways a backrest's panel lights it from behind. */
const BACKREST_LIGHTING_OPTIONS = [['none', 'None'], ['up', 'Up'], ['down', 'Down'], ['both', 'Up + down']] as const;

/** What a backrest's warnings say in its panel. */
const BACKREST_WARNING_TEXT: Record<BackrestWarning, () => string> = {
  door: () => tr('It runs across a door.'),
  heater: () => tr('It comes within 100 mm of the heater.'),
  pane: () => tr('It stands in front of a glass pane or an opening.'),
  partition: () => tr('It runs through a partition.'),
};
